import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { canAccess } from '../lib/permissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('hearclear_token');
    const storedUser = localStorage.getItem('hearclear_user');
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      // Verify token is still valid
      authAPI.getMe()
        .then((response) => {
          setUser(response.data);
          localStorage.setItem('hearclear_user', JSON.stringify(response.data));
        })
        .catch(() => {
          localStorage.removeItem('hearclear_token');
          localStorage.removeItem('hearclear_user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password) => {
    const response = await authAPI.login({ username, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('hearclear_token', token);
    localStorage.setItem('hearclear_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('hearclear_token');
    localStorage.removeItem('hearclear_user');
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isAccountant = () => user?.role === 'accountant' || user?.role === 'admin';
  const can = (module, action = 'view') => canAccess(user, module, action);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isAccountant, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
